import { useEffect, useState } from "react";
import { X, Download } from "lucide-react";

/**
 * PWAInstallBanner (V3.20.5)
 * Bandeau d'installation mobile pour Progressive Web App.
 * - S'affiche uniquement quand le navigateur envoie `beforeinstallprompt`
 *   (Android/Chrome/Edge/Samsung Internet).
 * - Sur iOS Safari, `beforeinstallprompt` n'existe pas → fallback avec
 *   instruction visuelle « Ajouter à l'écran d'accueil » (partager > + ).
 * - L'utilisateur peut fermer le bandeau (choix mémorisé 30 jours).
 * - Une fois installée, l'app émet `appinstalled` et le bandeau disparaît.
 */
const DISMISS_KEY = "africtal_pwa_dismissed_at";
const DISMISS_DAYS = 30;

function isIOSSafari() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
  return isIOS && isSafari;
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator?.standalone === true
  );
}

function wasRecentlyDismissed() {
  try {
    const t = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (!t) return false;
    return (Date.now() - t) / 86400000 < DISMISS_DAYS;
  } catch {
    return false;
  }
}

export default function PWAInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosMode, setIosMode] = useState(false);

  useEffect(() => {
    if (isStandalone() || wasRecentlyDismissed()) return;

    const onBIP = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };
    const onInstalled = () => {
      setVisible(false);
      setDeferredPrompt(null);
      try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
    };
    window.addEventListener("beforeinstallprompt", onBIP);
    window.addEventListener("appinstalled", onInstalled);

    // iOS Safari fallback : pas d'API, on affiche l'instruction après 8 s.
    let iosTimer;
    if (isIOSSafari()) {
      iosTimer = setTimeout(() => {
        setIosMode(true);
        setVisible(true);
      }, 8000);
    }
    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    setVisible(false);
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice.catch(() => null);
    setDeferredPrompt(null);
    if (choice?.outcome === "accepted") {
      setVisible(false);
    } else {
      dismiss();
    }
  };

  if (!visible) return null;

  return (
    <div className="afr-pwa-banner" role="dialog" aria-label="Installer Africtal"
         data-testid="pwa-install-banner">
      <img
        src="/brand/africtal-logo.png"
        alt=""
        className="afr-pwa-banner__logo"
        aria-hidden="true"
      />
      <div className="afr-pwa-banner__text">
        <strong>Installer Africtal</strong>
        {iosMode ? (
          <span>
            Ouvrez le menu <b>Partager</b>, puis <b>« Ajouter à l'écran d'accueil »</b>.
          </span>
        ) : (
          <span>Ajoutez l'appli à votre écran d'accueil pour un accès rapide.</span>
        )}
      </div>
      {!iosMode && deferredPrompt && (
        <button
          type="button"
          onClick={install}
          className="afr-pwa-banner__btn"
          data-testid="pwa-install-accept"
        >
          <Download size={14} /> Installer
        </button>
      )}
      <button
        type="button"
        onClick={dismiss}
        className="afr-pwa-banner__close"
        aria-label="Fermer le bandeau d'installation"
        data-testid="pwa-install-dismiss"
      >
        <X size={16} />
      </button>
    </div>
  );
}
